import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, BedDouble, Home as HomeIcon, ChevronRight, BellRing } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { useAppModals } from "./ValuationDialog";
import { PROPERTIES, PRICE_OPTIONS } from "../mock";

export default function Properties() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { openRegister } = useAppModals();
  const [filters, setFilters] = useState({ min: "", max: "", beds: "" });
  const [list, setList] = useState(PROPERTIES);
  const [results, setResults] = useState(PROPERTIES);
  const bedOptions = [...new Set(list.map((p) => p.beds ?? 0))].sort((a, b) => a - b);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/properties`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) {
          setList(res.data);
          setResults(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const num = (v) => (v ? parseInt(String(v).replace(/,/g, ""), 10) : null);

  const handleSearch = () => {
    const min = num(filters.min) ?? 0;
    const max = num(filters.max) ?? Infinity;
    const filtered = list.filter((p) => {
      const price = p.priceNum || 0;
      if (price && (price < min || price > max)) return false;
      if (filters.beds) {
        if (filters.beds === "studio") {
          if ((p.beds || 0) !== 0) return false;
        } else if ((p.beds || 0) < num(filters.beds)) return false;
      }
      return true;
    });
    setResults(filtered);
    toast({
      title: filtered.length ? `${filtered.length} propert${filtered.length === 1 ? "y" : "ies"} found` : "No matches",
      description: filtered.length
        ? "Showing properties that match your search."
        : "Try widening your budget, beds or property type.",
    });
    const el = document.getElementById("featured");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const clearFilters = () => {
    setFilters({ min: "", max: "", beds: "" });
    setResults(list);
  };
  const hasFilters = filters.min || filters.max || filters.beds;

  return (
    <section id="tenants" className="bg-[#faf7f0] py-16 lg:py-24">
      <div className="mx-auto max-w-[1200px] px-5">
        <div className="text-center">
          <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
            Find your next property
          </h2>
          <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
        </div>

        {/* Search bar */}
        <div className="mx-auto mt-8 grid max-w-3xl gap-3 rounded-[1.5rem] border border-[#e5ddd0] bg-white p-5 shadow-lg md:grid-cols-4">
          <Select onValueChange={(v) => setFilters({ ...filters, min: v })}>
            <SelectTrigger className="h-12">
              <SelectValue placeholder="Min Price" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {PRICE_OPTIONS.map((p) => (
                <SelectItem key={p} value={p}>{`\u00a3${p} pcm`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => setFilters({ ...filters, max: v })}>
            <SelectTrigger className="h-12">
              <SelectValue placeholder="Max Price" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {PRICE_OPTIONS.map((p) => (
                <SelectItem key={p} value={p}>{`\u00a3${p} pcm`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => setFilters({ ...filters, beds: v })}>
            <SelectTrigger className="h-12">
              <SelectValue placeholder="Min Beds" />
            </SelectTrigger>
            <SelectContent>
              {bedOptions.map((b) => (
                <SelectItem key={b} value={b === 0 ? "studio" : String(b)}>
                  {b === 0 ? "Studio" : b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={handleSearch}
            data-testid="property-search-button"
            className="flex h-12 items-center justify-center gap-2 rounded-full bg-[#ec008c] text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#c60074]"
          >
            <Search size={18} /> Search
          </button>
        </div>

        {/* Featured properties */}
        <div id="featured" className="mt-20 text-center">
          <h3 className="font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
            {hasFilters ? "Matching Properties" : "Featured Properties"}
          </h3>
          <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
          {hasFilters && (
            <button
              onClick={clearFilters}
              data-testid="clear-filters-button"
              className="mx-auto mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#16323f] underline underline-offset-4 hover:text-[#ec008c]"
            >
              Clear filters
            </button>
          )}
        </div>
        {results.length === 0 ? (
          <div data-testid="no-results" className="mt-10 rounded-[1.5rem] border border-dashed border-[#e5ddd0] bg-white/60 px-8 py-16 text-center">
            <p className="font-display text-xl font-600 text-[#16323f]">No properties match your search</p>
            <p className="mt-2 text-sm text-[#7a716a]">Try widening your budget, bedrooms or property type — or set up an alert and we'll tell you when one lands.</p>
            <button onClick={clearFilters} className="mt-6 inline-flex items-center gap-1 rounded-full border border-[#16323f]/20 px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#16323f] transition-colors hover:border-[#ec008c] hover:text-[#ec008c]">
              Show all properties
            </button>
          </div>
        ) : (
        <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {results.map((p) => (
            <article
              key={p.id}
              className="group overflow-hidden rounded-[1.5rem] border border-[#e5ddd0] bg-white shadow-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl"
            >
              <div className="relative overflow-hidden">
                <img
                  src={p.image}
                  onError={(e) => (e.currentTarget.src = p.fallback)}
                  alt={p.title}
                  className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <span className="absolute left-4 top-4 rounded-full bg-[#16323f] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white">
                  {p.tag}
                </span>
              </div>
              <div className="p-6">
                <h4 className="font-display text-xl font-500 text-[#16323f]">{p.title}</h4>
                <p className="mt-1 text-lg font-bold text-[#ec008c]">{p.price}</p>
                <div className="mt-3 flex gap-5 text-sm text-[#7a716a]">
                  <span className="flex items-center gap-1.5">
                    <BedDouble size={16} /> {p.beds === 0 ? "Studio" : `${p.beds} bed`}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <HomeIcon size={16} /> {p.type}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-[#5b5450]">{p.desc}</p>
                <button
                  onClick={() => navigate(`/property/${p.id}`)}
                  className="mt-5 inline-flex items-center gap-1 rounded-full border border-[#16323f]/20 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-[#16323f] transition-colors hover:border-[#ec008c] hover:text-[#ec008c]"
                >
                  Property Details
                  <ChevronRight size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>
        )}

        {/* Property alerts CTA */}
        <div
          data-testid="property-alerts-cta"
          className="mt-16 overflow-hidden rounded-[1.75rem] border border-[#e5ddd0] bg-[#16323f] px-8 py-12 text-center shadow-xl"
        >
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#ec008c] text-white">
            <BellRing size={26} />
          </span>
          <h3 className="mt-5 font-display text-2xl font-600 uppercase tracking-wide text-white lg:text-3xl">
            Never miss the right home
          </h3>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/70">
            Tell us your budget, bedrooms and favourite areas. We'll alert you the moment a matching
            property lands with New Space — often before it goes live anywhere else.
          </p>
          <button
            onClick={openRegister}
            data-testid="property-alerts-cta-button"
            className="group mx-auto mt-7 inline-flex items-center gap-2 rounded-full bg-[#ec008c] px-9 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]"
          >
            Register for property alerts
            <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
}
